import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { path: '/', label: 'nav.home' },
  { path: '/soins', label: 'nav.services' },
  { path: '/galerie', label: 'nav.gallery' },
  { path: '/univers', label: 'nav.about' },
  { path: '/temoignages', label: 'nav.testimonials' },
  { path: '/contact', label: 'nav.contact' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoAnimKey, setLogoAnimKey] = useState(0);
  const location = useLocation();

  const isHome = location.pathname === '/';

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 40);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Replay the logo animation each time the user arrives on the home page.
  // Bumping the key remounts the logo so the CSS keyframe fires again.
  useEffect(() => {
    if (location.pathname === '/') {
      setLogoAnimKey((k) => k + 1);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const bgClass = isHome
    ? scrolled
      ? 'bg-background-50/95 backdrop-blur-xl shadow-sm'
      : 'bg-transparent'
    : scrolled
      ? 'bg-background-50/95 backdrop-blur-xl shadow-sm'
      : 'bg-background-50/80 backdrop-blur-md';

  const textColor = isHome && !scrolled ? 'text-background-50' : 'text-foreground-800';

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${bgClass}`}>
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link
              key={isHome ? `logo-${logoAnimKey}` : 'logo-static'}
              to="/"
              className={`flex items-center gap-2.5 shrink-0 ${isHome ? 'logo-soft-in' : ''}`}
            >
              <img
                src="https://static.readdy.ai/image/66108e401cf688a02511c33e687ef000/027bb56ccb9ee3af4b09b48629b253d4.png"
                alt="KD'S BEAUTY"
                className="h-9 md:h-11 w-auto object-contain"
              />
              <span className={`hidden sm:block font-heading text-lg md:text-xl tracking-wide transition-colors duration-500 ${textColor}`}>
                KD'S BEAUTY
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-1 lg:gap-2">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`relative px-3 py-2 text-sm font-medium tracking-wide transition-all duration-300 rounded-lg group whitespace-nowrap ${
                      isActive
                        ? isHome && !scrolled
                          ? 'text-background-50 bg-background-50/15'
                          : 'text-primary-600 bg-primary-50'
                        : textColor
                    } hover:text-primary-500`}
                  >
                    {t(link.label)}
                  </Link>
                );
              })}
            </div>

            <div className="hidden md:block">
              <Link
                to="/rdv"
                className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                  isHome && !scrolled
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-sm" />
                </div>
                {t('nav.book')}
              </Link>
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden p-2 transition-colors rounded-lg ${textColor}`}
              aria-label="Menu"
            >
              <div className="w-6 h-5 relative flex flex-col justify-between">
                <span className={`block h-0.5 w-full transition-all duration-300 rounded-full ${isHome && !scrolled ? 'bg-background-50' : 'bg-foreground-800'} ${mobileOpen ? 'rotate-45 translate-y-[9px]' : ''}`} />
                <span className={`block h-0.5 w-full transition-all duration-300 rounded-full ${isHome && !scrolled ? 'bg-background-50' : 'bg-foreground-800'} ${mobileOpen ? 'opacity-0' : ''}`} />
                <span className={`block h-0.5 w-full transition-all duration-300 rounded-full ${isHome && !scrolled ? 'bg-background-50' : 'bg-foreground-800'} ${mobileOpen ? '-rotate-45 -translate-y-[9px]' : ''}`} />
              </div>
            </button>
          </div>
        </div>
      </nav>

      <div className={`fixed inset-0 z-40 bg-background-50 transition-transform duration-500 md:hidden ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col items-center justify-center h-full gap-6 px-8">
          <img
            src="https://static.readdy.ai/image/66108e401cf688a02511c33e687ef000/027bb56ccb9ee3af4b09b48629b253d4.png"
            alt="KD'S BEAUTY"
            className="h-16 w-auto object-contain mb-4"
          />
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="text-2xl font-heading text-foreground-800 hover:text-primary-500 transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              {t(link.label)}
            </Link>
          ))}
          <Link
            to="/rdv"
            className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full text-lg font-medium bg-green-600 text-white hover:bg-green-700 transition-all mt-4"
            onClick={() => setMobileOpen(false)}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-whatsapp-line text-lg" />
            </div>
            {t('nav.book')}
          </Link>
        </div>
      </div>
    </>
  );
}